/**
 * /register — an individual registering THEMSELVES for a Connect program.
 *
 * Not the shared parent/child enrolment journey: Connect registrants are
 * Year 12 leavers, and their email (magic-link sign-in) is who they are.
 *
 *   loading → no open program → signed out (email step, "check your inbox")
 *   → the form (name, mobile, the host's questions from host portal → Forms)
 *   → confirmation (ref, details, edit, withdraw).
 *
 * Which program: `?program=ID` when given, otherwise THE CURRENT PROGRAM
 * (lib/current-program.ts — the Worker applies the same rule). `noindex`:
 * /sessions is the page that should rank.
 */
import { useState, type FormEvent } from "react";
import { PageIntro } from "../components/PageIntro";
import { LOCATION_UNSURE, useBlock } from "../lib/content";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, CheckCircle2, Clock, MailCheck, MapPin } from "lucide-react";
import {
  apiClient,
  canEnrolNow,
  formatEventDates,
  type FormField,
  type Program,
} from "@alight/contracts";
import {
  Banner,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  Input,
  Label,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@alight/ui";
import { useSeo } from "../lib/seo";
import { useCurrentProgram } from "../lib/current-program";
import {
  ApiError,
  RegistrationError,
  getMyRegistrations,
  saveRegistration,
  withdrawRegistration,
  type Registration,
} from "../lib/registrations";

export const Route = createFileRoute("/register")({
  component: RegisterPage,
  validateSearch: (s: Record<string, unknown>): { program?: string } =>
    typeof s.program === "string" || typeof s.program === "number" ? { program: String(s.program) } : {},
});

const api = apiClient("");
const EMAIL_RE = /^\S+@\S+\.\S+$/;

/** Under ["my"] so the shared VerifyPage refreshes it after sign-in. */
export const MY_REGISTRATIONS_KEY = ["my", "connect-registrations"] as const;

export function useMe() {
  return useQuery({ queryKey: ["auth", "me"], queryFn: () => api.authMe(), retry: false });
}

export function useMyRegistrations(enabled: boolean) {
  return useQuery({ queryKey: MY_REGISTRATIONS_KEY, queryFn: getMyRegistrations, enabled, retry: false });
}

/* ---------------------------------------------------------------------------
 * Page
 * ------------------------------------------------------------------------- */

/** One choice in "Which location will you attend?", built from the CMS. */
interface LocationOption {
  /** What is stored — the location's name exactly as the host has it today. */
  value: string;
  /** Church / nearest station, so two similar names are never ambiguous. */
  detail: string;
}

function RegisterPage() {
  const banners = useBlock("banners");
  const locations = useBlock("locations");
  useSeo({ title: "Register | Connect Sydney", noindex: true });
  const search = Route.useSearch();
  const current = useCurrentProgram();
  const me = useMe();
  const email = me.data?.email ?? null;
  const mine = useMyRegistrations(Boolean(email));
  const [editing, setEditing] = useState(false);

  // The host's locations, live from Site content — rename one and this picker
  // (and what gets stored from here on) follows.
  const locationOptions: LocationOption[] = locations.items
    .map((l) => ({
      value: l.region.trim(),
      detail: [l.church.trim(), l.note.trim()].filter(Boolean).join(" · "),
    }))
    .filter((o) => o.value !== "");

  const program: Program | null = search.program
    ? (current.programs.find((p) => String(p.id) === search.program) ?? null)
    : current.program;
  const open = program !== null && canEnrolNow(program);

  const fields = useQuery({
    queryKey: ["form", program?.form_key ?? "enrolment"],
    queryFn: () => api.getForm(program?.form_key ?? "enrolment"),
    enabled: open,
    retry: false,
  });

  const existing = program ? (mine.data?.registrations.find((r) => r.program_id === program.id) ?? null) : null;
  const redirect = search.program ? `/register?program=${encodeURIComponent(search.program)}` : "/register";
  const dates = program ? formatEventDates(program.start_date, program.end_date) : "";

  const loading =
    current.isLoading || me.isLoading || (open && fields.isLoading) || (Boolean(email) && mine.isLoading);

  let body: React.ReactNode;
  if (loading) {
    body = <LoadingCard />;
  } else if (!program || !open) {
    body = <NotOpenCard />;
  } else if (!email) {
    body = (
      <Card>
        <CardContent className="p-6 sm:p-8">
          <h2 className="font-heading text-2xl font-extrabold text-ink">Start with your email</h2>
          <p className="mt-2 text-muted">
            We&apos;ll email you a sign-in link. It&apos;s also how you get back to your registration later — no
            password needed.
          </p>
          <div className="mt-6">
            <EmailSignIn redirect={redirect} />
          </div>
        </CardContent>
      </Card>
    );
  } else if (mine.isError || fields.isError) {
    body = (
      <Banner variant="error">
        We couldn&apos;t load your registration just now. Please refresh the page to try again.
      </Banner>
    );
  } else if (existing && !editing) {
    body = (
      <Confirmation
        program={program}
        registration={existing}
        fields={fields.data ?? []}
        onEdit={() => setEditing(true)}
      />
    );
  } else {
    const previous = existing ?? mine.data?.registrations[0] ?? null;
    body = (
      <RegistrationForm
        key={existing ? `edit-${existing.id}` : "new"}
        program={program}
        fields={fields.data ?? []}
        locationOptions={locationOptions}
        initial={previous}
        keepAnswers={Boolean(existing)}
        onSaved={() => setEditing(false)}
        onCancel={existing ? () => setEditing(false) : undefined}
      />
    );
  }

  return (
    <>
      <PageIntro
        image={banners.registerImage}
        imageAlt={banners.registerImageAlt}
        eyebrow="Register"
        title={program && open ? program.title : "Register for Connect"}
        description={
          program && open
            ? "Let us know you're coming — it takes a minute, and it helps us plan for you."
            : undefined
        }
      />
      <Section tone="paper" spacing="lg">
        <Container width="narrow" className="space-y-6 px-0 sm:px-0 lg:px-0">
          {program && open && !loading && <ProgramSummary program={program} dates={dates} />}
          {email && !loading && <SignedInBar email={email} />}
          {body}
        </Container>
      </Section>
    </>
  );
}

/* ---------------------------------------------------------------------------
 * Pieces
 * ------------------------------------------------------------------------- */

function LoadingCard() {
  return (
    <Card>
      <CardContent className="p-10 text-center text-sm text-muted" role="status">
        <span className="animate-pulse">Loading…</span>
      </CardContent>
    </Card>
  );
}

function NotOpenCard() {
  return (
    <Card>
      <CardContent className="p-6 text-center sm:p-10">
        <CalendarDays className="mx-auto h-10 w-10 text-primary" aria-hidden />
        <h2 className="mt-4 font-heading text-2xl font-extrabold text-ink">
          Registrations for the next Connect season aren&apos;t open yet
        </h2>
        <p className="mx-auto mt-2 max-w-md text-muted">
          Check the session dates, or get in touch and we&apos;ll let you know when they open.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button asChild>
            <Link to="/sessions">See the sessions</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/contact">Contact us</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ProgramSummary({ program, dates }: { program: Program; dates: string }) {
  return (
    <div className="grid gap-3 rounded-card border border-secondary/40 bg-band p-5 text-sm text-ink sm:grid-cols-2">
      {/* The host's schedule text usually carries the dates already. */}
      {dates && !program.schedule && (
        <p className="flex items-start gap-2">
          <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
          <span>{dates}</span>
        </p>
      )}
      {program.schedule && (
        <p className="flex items-start gap-2">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
          <span className="whitespace-pre-line">{program.schedule}</span>
        </p>
      )}
      {program.location && (
        <p className="flex items-start gap-2 sm:col-span-2">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
          <span className="whitespace-pre-line">{program.location}</span>
        </p>
      )}
    </div>
  );
}

export function SignedInBar({ email }: { email: string }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  async function signOut() {
    setBusy(true);
    try {
      await api.signOut();
    } catch {
      // Clearing local state is still the right thing to show.
    }
    qc.setQueryData(["auth", "me"], { email: null, admin: false });
    qc.removeQueries({ queryKey: MY_REGISTRATIONS_KEY });
    setBusy(false);
  }
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted">
      <span className="min-w-0 break-all">
        Signed in as <span className="font-semibold text-ink">{email}</span>
      </span>
      <span aria-hidden>·</span>
      <button
        type="button"
        onClick={() => void signOut()}
        disabled={busy}
        className="font-semibold text-primary underline-offset-2 hover:underline disabled:opacity-60"
      >
        Sign out
      </button>
    </p>
  );
}

/** Keep only path + query so a dev echo link works on whatever origin this is. */
function localLink(link: string): string {
  try {
    const u = new URL(link);
    return `${u.pathname}${u.search}`;
  } catch {
    return link;
  }
}

export function EmailSignIn({ redirect }: { redirect: string }) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<{ email: string; emailed: boolean; devLink?: string } | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError(null);
    setSending(true);
    try {
      const res = await api.requestLink({ email: value, redirect });
      setSent({ email: value, emailed: res.emailed, devLink: res.devLink });
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "Something went wrong — please try again.");
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div role="status" className="rounded-card border border-success/30 bg-success/10 p-6 text-center">
        <MailCheck className="mx-auto h-10 w-10 text-success" aria-hidden />
        <h3 className="mt-3 font-heading text-xl font-bold text-ink">Check your inbox</h3>
        <p className="mt-2 break-words text-ink">
          We&apos;ve sent a sign-in link to <span className="font-semibold">{sent.email}</span>.
        </p>
        <p className="mt-1 text-sm text-muted">
          Open it on this device to carry on. It works once and expires in 15 minutes — check your junk folder if
          it hasn&apos;t arrived.
        </p>
        {import.meta.env.DEV && sent.devLink && (
          <p className="mt-4 text-sm">
            <a href={localLink(sent.devLink)} className="font-semibold text-primary underline" data-dev-link>
              Dev only: open the sign-in link
            </a>
          </p>
        )}
        <Button type="button" variant="outline" size="sm" className="mt-4" onClick={() => setSent(null)}>
          Use a different email
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="signin-email">Email</Label>
        <Input
          id="signin-email"
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={sending}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? "signin-email-error" : undefined}
        />
        {error && (
          <p id="signin-email-error" className="text-sm text-error">
            {error}
          </p>
        )}
      </div>
      <Button type="submit" disabled={sending}>
        {sending ? "Sending…" : "Email me a sign-in link"}
      </Button>
    </form>
  );
}

/* ---------------------------------------------------------------------------
 * Form
 * ------------------------------------------------------------------------- */

/** `X_other` is the follow-up to the select `X` (the Forms-screen convention). */
function followUpParent(field: FormField, fields: FormField[]): FormField | null {
  if (!field.name.endsWith("_other")) return null;
  const parentName = field.name.slice(0, -"_other".length);
  return fields.find((f) => f.name === parentName && f.type === "select") ?? null;
}

function selectPlaceholder(label: string): string {
  const t = label.trim();
  return t.endsWith("?") ? "Choose an option" : `Select ${t.toLowerCase()}`;
}

interface FormValues {
  first_name: string;
  last_name: string;
  phone: string;
  location: string;
  answers: Record<string, string>;
}

function RegistrationForm({
  program,
  fields,
  locationOptions,
  initial,
  keepAnswers,
  onSaved,
  onCancel,
}: {
  program: Program;
  fields: FormField[];
  locationOptions: LocationOption[];
  /** Details to start from — their registration being edited, or a previous one. */
  initial: Registration | null;
  /** Prefill the answers too (editing). A different program starts them blank. */
  keepAnswers: boolean;
  onSaved: (reg: Registration) => void;
  onCancel?: () => void;
}) {
  const qc = useQueryClient();
  const offeredLocations = new Set([...locationOptions.map((o) => o.value), LOCATION_UNSURE]);
  const [values, setValues] = useState<FormValues>({
    first_name: initial?.first_name ?? "",
    last_name: initial?.last_name ?? "",
    phone: initial?.phone ?? "",
    // A location that has since been renamed away is no longer on offer, so
    // ask again rather than silently re-submitting a name nobody uses.
    location: initial && offeredLocations.has(initial.location) ? initial.location : "",
    answers: keepAnswers && initial ? { ...initial.answers } : {},
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const visible = fields.filter((f) => {
    const parent = followUpParent(f, fields);
    return !parent || values.answers[parent.name] === "Other";
  });

  const clearError = (key: string) =>
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  const setTop = (key: "first_name" | "last_name" | "phone" | "location", v: string) => {
    setValues((prev) => ({ ...prev, [key]: v }));
    clearError(key);
  };
  const setAnswer = (name: string, v: string) => {
    setValues((prev) => ({ ...prev, answers: { ...prev.answers, [name]: v } }));
    clearError(name);
  };

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!values.first_name.trim()) e.first_name = "Please enter your first name.";
    if (!values.last_name.trim()) e.last_name = "Please enter your last name.";
    if (!values.location) e.location = "Please choose the location you'll attend.";
    for (const f of visible) {
      const v = (values.answers[f.name] ?? "").trim();
      if (!v) {
        if (f.required) e[f.name] = f.type === "select" ? "Please choose an option." : "This question needs an answer.";
        continue;
      }
      if (f.type === "select" && f.options.length > 0 && !f.options.includes(v)) e[f.name] = "Please choose one of the options.";
      if (f.type === "email" && !EMAIL_RE.test(v)) e[f.name] = "Please enter a valid email address.";
    }
    return e;
  }

  function focusFirst(errs: Record<string, string>) {
    const order = ["first_name", "last_name", "phone", "location", ...visible.map((f) => f.name)];
    const first = order.find((k) => errs[k]);
    if (first) document.getElementById(`reg-${first}`)?.focus();
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setFormError("Please check the highlighted answers.");
      focusFirst(errs);
      return;
    }
    const answers: Record<string, string> = {};
    for (const f of visible) {
      const v = (values.answers[f.name] ?? "").trim();
      if (v) answers[f.name] = v;
    }
    setSaving(true);
    try {
      const { registration } = await saveRegistration({
        program_id: program.id,
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        phone: values.phone.trim(),
        location: values.location,
        answers,
      });
      await qc.invalidateQueries({ queryKey: MY_REGISTRATIONS_KEY });
      onSaved(registration);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      if (err instanceof RegistrationError) {
        setErrors(err.fields);
        setFormError(err.message);
        focusFirst(err.fields);
      } else {
        setFormError(
          err instanceof ApiError && err.status > 0
            ? err.message
            : "Something went wrong saving your registration. Please check your connection and try again.",
        );
      }
    } finally {
      setSaving(false);
    }
  }

  const errorText = (key: string) =>
    errors[key] ? (
      <p id={`reg-${key}-error`} className="text-sm text-error">
        {errors[key]}
      </p>
    ) : null;
  const a11y = (key: string) => ({
    "aria-invalid": errors[key] ? true : undefined,
    "aria-describedby": errors[key] ? `reg-${key}-error` : undefined,
  });

  return (
    <Card>
      <CardContent className="p-6 sm:p-8">
        <h2 className="font-heading text-2xl font-extrabold text-ink">
          {onCancel ? "Edit your details" : "Your details"}
        </h2>
        <form onSubmit={submit} noValidate className="mt-6 space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="reg-first_name">First name</Label>
              <Input
                id="reg-first_name"
                autoComplete="given-name"
                value={values.first_name}
                onChange={(e) => setTop("first_name", e.target.value)}
                disabled={saving}
                maxLength={80}
                {...a11y("first_name")}
              />
              {errorText("first_name")}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-last_name">Last name</Label>
              <Input
                id="reg-last_name"
                autoComplete="family-name"
                value={values.last_name}
                onChange={(e) => setTop("last_name", e.target.value)}
                disabled={saving}
                maxLength={80}
                {...a11y("last_name")}
              />
              {errorText("last_name")}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="reg-phone">
              Mobile <span className="font-normal text-muted">(optional)</span>
            </Label>
            <Input
              id="reg-phone"
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              className="sm:max-w-xs"
              value={values.phone}
              onChange={(e) => setTop("phone", e.target.value)}
              disabled={saving}
              maxLength={40}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="reg-location">Which location will you attend?</Label>
            <p id="reg-location-desc" className="text-sm text-muted">
              Come to whichever is closest — you can change this later, or turn up somewhere else on the night.
            </p>
            <Select value={values.location} onValueChange={(v) => setTop("location", v)} disabled={saving}>
              <SelectTrigger
                id="reg-location"
                className="w-full"
                aria-invalid={errors.location ? true : undefined}
                aria-describedby={
                  errors.location ? "reg-location-desc reg-location-error" : "reg-location-desc"
                }
              >
                <SelectValue placeholder="Choose a location" />
              </SelectTrigger>
              <SelectContent>
                {locationOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    <span className="font-medium">{o.value}</span>
                    {o.detail && <span className="text-muted"> — {o.detail}</span>}
                  </SelectItem>
                ))}
                <SelectItem value={LOCATION_UNSURE}>{LOCATION_UNSURE}</SelectItem>
              </SelectContent>
            </Select>
            {errorText("location")}
          </div>

          {visible.map((f) => (
            <DynamicField
              key={f.name}
              field={f}
              value={values.answers[f.name] ?? ""}
              onChange={(v) => setAnswer(f.name, v)}
              disabled={saving}
              error={errors[f.name]}
            />
          ))}

          {formError && <Banner variant="error">{formError}</Banner>}

          <div className="flex flex-wrap gap-3">
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : onCancel ? "Save changes" : "Register"}
            </Button>
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function DynamicField({
  field,
  value,
  onChange,
  disabled,
  error,
}: {
  field: FormField;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  error?: string;
}) {
  const id = `reg-${field.name}`;
  const describedBy = [field.description ? `${id}-desc` : "", error ? `${id}-error` : ""].filter(Boolean).join(" ");
  const common = {
    id,
    disabled,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": describedBy || undefined,
  };
  const optional = !field.required && <span className="font-normal text-muted"> (optional)</span>;

  let control: React.ReactNode;
  switch (field.type) {
    case "textarea":
      control = <Textarea {...common} rows={4} maxLength={2000} value={value} onChange={(e) => onChange(e.target.value)} />;
      break;
    case "select":
      control = (
        <Select value={value} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger {...common} className="w-full">
            <SelectValue placeholder={selectPlaceholder(field.label)} />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((o) => (
              <SelectItem key={o} value={o}>
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
      break;
    case "checkbox":
      return (
        <div className="space-y-1.5">
          <div className="flex items-start gap-3">
            <Checkbox
              {...common}
              checked={value === "yes"}
              onCheckedChange={(v) => onChange(v === true ? "yes" : "")}
            />
            <Label htmlFor={id} className="font-normal leading-snug">
              {field.label}
              {optional}
            </Label>
          </div>
          {field.description && (
            <p id={`${id}-desc`} className="text-sm text-muted">
              {field.description}
            </p>
          )}
          {error && (
            <p id={`${id}-error`} className="text-sm text-error">
              {error}
            </p>
          )}
        </div>
      );
    default: {
      const type = ["email", "tel", "number", "date"].includes(field.type) ? field.type : "text";
      control = (
        <Input {...common} type={type} maxLength={300} value={value} onChange={(e) => onChange(e.target.value)} />
      );
    }
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {field.label}
        {optional}
      </Label>
      {field.description && (
        <p id={`${id}-desc`} className="text-sm text-muted">
          {field.description}
        </p>
      )}
      {control}
      {error && (
        <p id={`${id}-error`} className="text-sm text-error">
          {error}
        </p>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Confirmation
 * ------------------------------------------------------------------------- */

function Confirmation({
  program,
  registration,
  fields,
  onEdit,
}: {
  program: Program;
  registration: Registration;
  fields: FormField[];
  onEdit: () => void;
}) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const withdrawn = registration.status === "withdrawn";

  async function withdraw() {
    setBusy(true);
    setError(null);
    try {
      await withdrawRegistration(registration.id);
      await qc.invalidateQueries({ queryKey: MY_REGISTRATIONS_KEY });
      setConfirming(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't withdraw just now — please try again.");
    } finally {
      setBusy(false);
    }
  }

  const details: Array<[string, string]> = [
    ["Name", `${registration.first_name} ${registration.last_name}`],
    ["Email", registration.email],
  ];
  if (registration.phone) details.push(["Mobile", registration.phone]);
  if (registration.location) details.push(["Location", registration.location]);
  for (const f of fields) {
    const v = registration.answers[f.name];
    if (!v) continue;
    details.push([f.label, f.type === "checkbox" ? "Yes" : v]);
  }

  return (
    <Card>
      <CardContent className="p-6 sm:p-8">
        {withdrawn ? (
          <div className="text-center">
            <h2 className="font-heading text-2xl font-extrabold text-ink">You&apos;ve withdrawn</h2>
            <p className="mt-2 text-muted">
              You&apos;re no longer registered for {program.title}. Changed your mind? You can register again any time
              while registrations are open.
            </p>
            <Button type="button" className="mt-5" onClick={onEdit}>
              Register again
            </Button>
          </div>
        ) : (
          <div className="text-center" role="status">
            <CheckCircle2 className="mx-auto h-12 w-12 text-success" aria-hidden />
            <h2 className="mt-3 font-heading text-2xl font-extrabold text-ink">You&apos;re registered!</h2>
            <p className="mt-2 text-ink">
              Your reference is{" "}
              <span className="font-mono font-semibold" data-testid="registration-ref">
                {registration.ref}
              </span>
              .
            </p>
            <p className="mt-1 text-sm text-muted">
              We can&apos;t wait to see you. You can come back here any time to update your details.
            </p>
          </div>
        )}

        <dl className="mt-8 divide-y divide-border rounded-card border border-border">
          {details.map(([label, value]) => (
            <div key={label} className="grid gap-1 px-4 py-3 sm:grid-cols-[minmax(0,14rem)_1fr] sm:gap-4">
              <dt className="text-sm font-medium text-muted">{label}</dt>
              <dd className="break-words text-ink">{value}</dd>
            </div>
          ))}
        </dl>

        {error && (
          <div className="mt-4">
            <Banner variant="error">{error}</Banner>
          </div>
        )}

        {!withdrawn && (
          <div className="mt-6">
            {confirming ? (
              <div className="rounded-card border border-warning bg-warning/10 p-4">
                <p className="font-semibold text-ink">Withdraw from {program.title}?</p>
                <p className="mt-1 text-sm text-muted">You can register again later if you change your mind.</p>
                <div className="mt-3 flex flex-wrap gap-3">
                  <Button type="button" variant="accent" size="sm" onClick={() => void withdraw()} disabled={busy}>
                    {busy ? "Withdrawing…" : "Yes, withdraw"}
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setConfirming(false)} disabled={busy}>
                    Keep my place
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3">
                <Button type="button" onClick={onEdit}>
                  Edit my details
                </Button>
                <Button type="button" variant="outline" onClick={() => setConfirming(true)}>
                  Withdraw
                </Button>
                <Button asChild variant="ghost">
                  <Link to="/sessions">Dates &amp; locations</Link>
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
